import { POST_DESCRIPTION_LIMIT, POST_TITLE_LIMIT } from '../constants';

/**
 * Advertising volunteering opportunities in the feed — shared by /partnerships, which links to
 * the composer, and the composer, which prefills from the roles picked.
 */

export const VOLUNTEER_CALL = 'VOLUNTEER_CALL';

export type AdvertisableRole = { id: number; title: string; type?: string | null };

/** The composer link for advertising one or more roles. */
export const volunteerCallComposeUrl = (composePath: string, roleIds: number[]): string =>
    `${composePath}?type=${VOLUNTEER_CALL}&roles=${roleIds.join(',')}`;

/** `?roles=3,7` → [3, 7], ignoring anything that isn't a positive integer. */
export const parseRoleIds = (value: string | null): number[] =>
    (value ?? '')
        .split(',')
        .map((part) => Number(part.trim()))
        .filter((id) => Number.isInteger(id) && id > 0);

const clip = (text: string, limit: number): string =>
    text.length <= limit ? text : `${text.slice(0, limit - 1).trimEnd()}…`;

/**
 * The prefilled title and description.
 *
 * Worded to read correctly on **every** app build: ones that predate this post type show it as an
 * ordinary update with no "I'll help" button, so the text points at the Impact tab, which every
 * build has, rather than at a button only newer builds draw.
 */
export const volunteerCallText = (roles: AdvertisableRole[]): { title: string; description: string } => {
    if (roles.length === 0) return { title: '', description: '' };

    if (roles.length === 1) {
        const [role] = roles;
        const kind = role.type ? ` (${role.type.toLowerCase()})` : '';
        return {
            title: clip(`Volunteer needed: ${role.title}`, POST_TITLE_LIMIT),
            description: clip(
                `We're looking for someone to help with ${role.title}${kind}. `
                + `If you have the time and the heart for it, put your hand up — `
                + `you'll find it, and every other way to get involved, on the Impact tab.`,
                POST_DESCRIPTION_LIMIT,
            ),
        };
    }

    const list = roles.map((role) => `• ${role.title}${role.type ? ` (${role.type.toLowerCase()})` : ''}`).join('\n');
    return {
        title: clip('Volunteers needed', POST_TITLE_LIMIT),
        description: clip(
            `We're looking for help with:\n${list}\n\nPut your hand up for any of these on the Impact tab.`,
            POST_DESCRIPTION_LIMIT,
        ),
    };
};

/** "today", "yesterday", "3 days ago", "5 weeks ago". Null for a missing or unreadable date. */
export const advertisedAgo = (iso: string | null | undefined, now: Date = new Date()): string | null => {
    if (!iso) return null;
    const then = new Date(iso);
    if (Number.isNaN(then.getTime())) return null;

    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const days = Math.round((startOfDay(now) - startOfDay(then)) / (24 * 60 * 60 * 1000));

    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 14) return `${days} days ago`;
    if (days < 60) return `${Math.round(days / 7)} weeks ago`;
    return `${Math.round(days / 30)} months ago`;
};
